import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import CreateProjectModal from './CreateProjectModal';
import ImportExcelModal from './ImportExcelModal';
import ProjectEditModal from './ProjectEditModal';
import ProjectCard from './ProjectCard';
import RoleGuard from './RoleGuard';
import NavigationBar from './NavigationBar';
import { useSystemSettings } from '../hooks/useSystemSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Компонент для канбан контейнера
const KanbanContainer = ({ 
  stages, 
  projects, 
  getProjectsByStage, 
  handleDragOver, 
  handleDrop, 
  handleDragStart, 
  handleProjectUpdated, 
  draggedProject,
  lostColumnCollapsed,
  setLostColumnCollapsed
}) => {
  return (
    <>
      {stages.map((stage) => {
        const stageProjects = getProjectsByStage(stage.id);
        const isLostColumn = stage.id === 'lost';
        
        return (
          <div
            key={stage.id}
            className={`kanban-column flex-shrink-0 ${isLostColumn && lostColumnCollapsed ? 'w-20' : 'w-80'} p-4 ${stage.color} h-full overflow-hidden flex flex-col transition-all duration-300`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
            data-testid={`kanban-column-${stage.id}`}
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              {isLostColumn ? (
                /* Lost column header with collapse button */
                <div className="flex items-center space-x-2 w-full">
                  <button
                    onClick={() => setLostColumnCollapsed(!lostColumnCollapsed)}
                    className="flex items-center space-x-2 hover:bg-white/30 rounded-lg p-1 transition-colors"
                    title={lostColumnCollapsed ? 'Развернуть колонку проигранных проектов' : 'Свернуть колонку проигранных проектов'}
                  >
                    <svg 
                      className={`w-4 h-4 transition-transform ${lostColumnCollapsed ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {lostColumnCollapsed ? (
                      <span className="text-xs font-medium transform rotate-90 whitespace-nowrap">
                        Проиграно ({stageProjects.length})
                      </span>
                    ) : (
                      <>
                        <h3 className="font-semibold text-gray-900">{stage.title}</h3>
                        <span className="bg-white/50 px-2 py-1 rounded-full text-sm font-medium text-gray-600">
                          {stageProjects.length}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Regular column header */
                <>
                  <h3 className="font-semibold text-gray-900">{stage.title}</h3>
                  <span className="bg-white/50 px-2 py-1 rounded-full text-sm font-medium text-gray-600">
                    {stageProjects.length}
                  </span>
                </>
              )}
            </div>
            
            {/* Projects container - hidden when lost column is collapsed */}
            {(!isLostColumn || !lostColumnCollapsed) && (
              <div className="flex-1 overflow-y-auto space-y-3 pr-2" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                {stageProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onDragStart={handleDragStart}
                    onUpdate={handleProjectUpdated}
                    isDragging={draggedProject?.id === project.id}
                  />
                ))}
                
                {stageProjects.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-sm">Нет проектов</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

const KanbanBoard = () => {
  const { user, logout } = useAuth();
  const { stages: systemStages } = useSystemSettings();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [draggedProject, setDraggedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lostColumnCollapsed, setLostColumnCollapsed] = useState(true); // Collapse lost column by default

  // Use dynamic stages from system settings with fallback colors
  const getStageColor = (stageId) => {
    const colorMap = {
      'research': 'bg-yellow-100',
      'pre_tender': 'bg-blue-100', 
      'tender_preparation': 'bg-purple-100',
      'commercial_proposal': 'bg-green-100',
      'negotiations': 'bg-orange-100',
      'won': 'bg-emerald-100',
      'lost': 'bg-red-100'
    };
    return colorMap[stageId] || 'bg-gray-100';
  };

  const stages = systemStages.map(stage => ({
    ...stage,
    color: getStageColor(stage.id)
  }));

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (project.name && project.name.toLowerCase().includes(query)) ||
      (project.customer_company && project.customer_company.toLowerCase().includes(query)) ||
      (project.city && project.city.toLowerCase().includes(query)) ||
      (project.assigned_manager_name && project.assigned_manager_name.toLowerCase().includes(query))
    );
  });

  const getProjectsByStage = (stageId) => {
    return filteredProjects.filter(project => project.stage === stageId);
  };

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedProject || draggedProject.stage === targetStage) {
      setDraggedProject(null);
      return;
    }

    try {
      await axios.put(`${API}/projects/${draggedProject.id}`, {
        ...draggedProject,
        stage: targetStage
      });
      
      loadProjects();
    } catch (error) {
      console.error('Failed to update project stage:', error);
      alert('Не удалось обновить этап проекта');
    }
    
    setDraggedProject(null);
  };

  const handleProjectCreated = (newProject) => {
    setProjects([...projects, newProject]);
    setShowCreateModal(false);
    loadProjects();
  };

  const handleProjectUpdated = (updatedProject) => {
    setProjects(projects.map(p => 
      p.id === updatedProject.id ? updatedProject : p
    ));
    loadProjects();
  };

  const handleImportComplete = (result) => {
    setShowImportModal(false);
    loadProjects();
    alert(`Импорт завершен! Добавлено проектов: ${result.imported || 0}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <NavigationBar />

      {/* Main Content */}
      <main className="max-w-full py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900" data-testid="kanban-title">
                  Канбан доска проектов
                </h1>
                <p className="text-gray-600 mt-2">
                  {searchQuery ? (
                    <>Найдено проектов: {filteredProjects.length} из {projects.length}</>
                  ) : (
                    <>Всего проектов: {projects.length}</>
                  )}
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <RoleGuard allowedRoles={['admin', 'lead']}>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="btn-secondary"
                    data-testid="import-excel-button"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Импорт Excel
                  </button>
                </RoleGuard>
                
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                  data-testid="create-project-button"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Создать проект
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mt-4 relative max-w-md">
              <input
                type="text"
                placeholder="Поиск проектов..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-input"
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Kanban Columns Container */}
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div className="flex gap-6 p-6 h-full" style={{ minWidth: 'max-content' }}>
              <KanbanContainer 
                stages={stages}
                projects={projects}
                getProjectsByStage={getProjectsByStage}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                handleDragStart={handleDragStart}
                handleProjectUpdated={handleProjectUpdated}
                draggedProject={draggedProject}
                lostColumnCollapsed={lostColumnCollapsed}
                setLostColumnCollapsed={setLostColumnCollapsed}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <ImportExcelModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
};

export default KanbanBoard;